/**
 * Dashboard API module (APP-001 §9, §10).
 *
 * Thin, typed wrappers over the dashboard backend-for-frontend routes. Each
 * function returns the generated DTO type and throws `ApiError` on failure.
 * Only the account bootstrap is exercised in APP-001; the remaining functions
 * are defined as the stable extension points feature packages (APP-003+) will
 * consume.
 */
import { apiRequest } from './client';
import type {
  AccountPatchRequest,
  AccountResponse,
  APIKeyCreatedResponse,
  APIKeyCreateRequest,
  APIKeyListResponse,
  APIKeyMetadata,
  APIKeyPatchRequest,
  BillingSummary,
  CheckoutRequest,
  CheckoutResponse,
  DashboardIdentityResponse,
  OverviewResponse,
  PlaygroundRequest,
  PlaygroundResponse,
  PlanChangeRequest,
  PlanChangeResponse,
  PortalResponse,
  PublicPlaygroundRequest,
  PublicPlaygroundResponse,
  UsageHistoryResponse,
  UsageSummaryResponse,
} from './dashboard-types';

const DASHBOARD = '/v1/dashboard';

/* ----- Account / bootstrap (APP-001 §9) ----- */

/** Minimum backend bootstrap request (APP-001 §9). */
export function getAccountIdentity(signal?: AbortSignal): Promise<DashboardIdentityResponse> {
  return apiRequest<DashboardIdentityResponse>(`${DASHBOARD}/account`, { signal }).then((r) => r.data);
}

export function patchAccount(body: AccountPatchRequest, signal?: AbortSignal): Promise<AccountResponse> {
  return apiRequest<AccountResponse>(`${DASHBOARD}/account`, { method: 'PATCH', body, signal }).then(
    (r) => r.data,
  );
}

/* ----- Overview (APP-003 surface; defined here as a stable extension point) ----- */

export function getOverview(signal?: AbortSignal): Promise<OverviewResponse> {
  return apiRequest<OverviewResponse>(`${DASHBOARD}/overview`, { signal }).then((r) => r.data);
}

/* ----- Usage ----- */

export function getUsageSummary(signal?: AbortSignal): Promise<UsageSummaryResponse> {
  return apiRequest<UsageSummaryResponse>(`${DASHBOARD}/usage`, { signal }).then((r) => r.data);
}

export function getUsageHistory(
  params?: { granularity?: 'daily' | 'monthly'; from?: string; to?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<UsageHistoryResponse> {
  return apiRequest<UsageHistoryResponse>(`${DASHBOARD}/usage/history`, { query: params, signal }).then(
    (r) => r.data,
  );
}

/* ----- API keys (APP-004 surface) ----- */

export function listApiKeys(params?: { limit?: number; offset?: number }, signal?: AbortSignal): Promise<APIKeyListResponse> {
  return apiRequest<APIKeyListResponse>(`${DASHBOARD}/api-keys`, { query: params, signal }).then((r) => r.data);
}

export function createApiKey(body: APIKeyCreateRequest, idempotencyKey: string, signal?: AbortSignal): Promise<APIKeyCreatedResponse> {
  return apiRequest<APIKeyCreatedResponse>(`${DASHBOARD}/api-keys`, {
    method: 'POST',
    body,
    idempotencyKey,
    signal,
  }).then((r) => r.data);
}

export function patchApiKey(id: string, body: APIKeyPatchRequest, signal?: AbortSignal): Promise<APIKeyMetadata> {
  return apiRequest<APIKeyMetadata>(`${DASHBOARD}/api-keys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    signal,
  }).then((r) => r.data);
}

export function rotateApiKey(id: string, idempotencyKey: string, signal?: AbortSignal): Promise<APIKeyCreatedResponse> {
  return apiRequest<APIKeyCreatedResponse>(`${DASHBOARD}/api-keys/${encodeURIComponent(id)}/rotate`, {
    method: 'POST',
    idempotencyKey,
    signal,
  }).then((r) => r.data);
}

export function revokeApiKey(id: string, signal?: AbortSignal): Promise<APIKeyMetadata> {
  return apiRequest<APIKeyMetadata>(`${DASHBOARD}/api-keys/${encodeURIComponent(id)}/revoke`, {
    method: 'POST',
    signal,
  }).then((r) => r.data);
}

/* ----- Billing (APP-006 surface) ----- */

export function getBilling(signal?: AbortSignal): Promise<BillingSummary> {
  return apiRequest<BillingSummary>(`${DASHBOARD}/billing`, { signal }).then((r) => r.data);
}

export function createCheckout(body: CheckoutRequest, idempotencyKey: string, signal?: AbortSignal): Promise<CheckoutResponse> {
  return apiRequest<CheckoutResponse>(`${DASHBOARD}/billing/checkout`, {
    method: 'POST',
    body,
    idempotencyKey,
    signal,
  }).then((r) => r.data);
}

/**
 * Cancel the account's user-abandoned pending Checkout
 * (POST /v1/dashboard/billing/checkout/cancel). The backend expires every
 * still-open Stripe Checkout Session for the account (so it can never complete
 * afterwards), clears the local `checkout_pending` marker that blocks new
 * Checkouts with 409, and returns the fresh summary (same schema as GET
 * /v1/dashboard/billing). No body and no idempotency key — the account is
 * derived from the verified Clerk session and the endpoint is idempotent (a
 * no-op returning the current view when nothing is pending).
 */
export function cancelPendingCheckout(signal?: AbortSignal): Promise<BillingSummary> {
  return apiRequest<BillingSummary>(`${DASHBOARD}/billing/checkout/cancel`, { method: 'POST', signal }).then(
    (r) => r.data,
  );
}

export function createPortal(signal?: AbortSignal): Promise<PortalResponse> {
  return apiRequest<PortalResponse>(`${DASHBOARD}/billing/portal`, { method: 'POST', signal }).then(
    (r) => r.data,
  );
}

/**
 * Change the plan of an existing active/trialing subscription in-app
 * (POST /v1/dashboard/billing/plan-change). The body is structurally
 * `{ plan, interval }` (same authority shape as Checkout — the backend resolves
 * the target Price from the trusted catalog and rejects any other field with
 * 400). Like Checkout, the backend REQUIRES an `X-Idempotency-Key` header, so
 * the caller generates a fresh key per submission attempt. Returns the fresh
 * billing summary plus the `change` outcome (`immediate` upgrade applied now /
 * `scheduled` downgrade landing at `effective_at`); the backend remains the
 * authority on ranking, proration, and scheduling.
 */
export function planChange(
  body: PlanChangeRequest,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<PlanChangeResponse> {
  return apiRequest<PlanChangeResponse>(`${DASHBOARD}/billing/plan-change`, {
    method: 'POST',
    body,
    idempotencyKey,
    signal,
  }).then((r) => r.data);
}

/**
 * Force an on-demand, account-scoped reconciliation against Stripe
 * (POST /v1/dashboard/billing/reconcile). Repairs the missed-webhook case (a
 * subscription stuck at `checkout_pending`/`incomplete`) by mirroring the
 * customer's authoritative Stripe state, and returns the fresh summary (same
 * schema as GET /v1/dashboard/billing). No body and no idempotency key — the
 * endpoint is idempotent by design and the account is derived from the
 * verified Clerk session.
 */
export function reconcileBilling(signal?: AbortSignal): Promise<BillingSummary> {
  return apiRequest<BillingSummary>(`${DASHBOARD}/billing/reconcile`, { method: 'POST', signal }).then(
    (r) => r.data,
  );
}

/* ----- Bulk + webhook summaries ----- */

// APP-007: the bulk wrappers (list/create/status/download) live in
// src/api/bulk.ts. `listBulkJobs` is re-exported here so existing import sites
// (dashboard overview) stay stable.
export { listBulkJobs } from './bulk';

// APP-008: the webhook wrappers (list/get/create/patch/delete/rotate/deliveries/
// test) live in src/api/webhooks.ts. `listWebhooks` is re-exported here so
// existing import sites (dashboard overview) stay stable.
export { listWebhooks } from './webhooks';

/* ----- Playground (APP-005 surface; consumes real quota) ----- */

export function runPlayground(body: PlaygroundRequest, signal?: AbortSignal): Promise<PlaygroundResponse> {
  return apiRequest<PlaygroundResponse>(`${DASHBOARD}/playground/analyze`, {
    method: 'POST',
    body,
    signal,
  }).then((r) => r.data);
}

/* ----- Public playground (anonymous pre-signup surface) ----- */

// Anonymous: no Clerk bearer token is attached (the caller has no session).
// Abuse is bounded server-side by the per-IP daily limit (default 10/day).
export function runPublicPlayground(
  body: PublicPlaygroundRequest,
  signal?: AbortSignal,
): Promise<PublicPlaygroundResponse> {
  return apiRequest<PublicPlaygroundResponse>('/v1/public/playground/analyze', {
    method: 'POST',
    body,
    signal,
    authenticated: false,
  }).then((r) => r.data);
}
